/**
 * The ONE rule for "which VAT percentage applies to this line", and the VAT
 * and gross that follow from it, on the six document line grids.
 *
 * The server sends a saved line's rate as an id (`vat_rate`) plus
 * `vat_rate_display` and `custom_vat_percentage` -- never the percentage
 * itself. The percentage is looked up by the VAT picker (VatRateSelect's
 * onResolve / onChange) into each screen's cache. Four grids read that cache;
 * the credit and debit notes read `vat_rate_percentage || 0` instead, so
 * retyping a saved line's quantity showed VAT 0.00 on a 13% line
 * (credit-debit-note-vat-drops-to-zero, 2026-09-24).
 *
 * Same order as the server's InvoiceLineItem.calculate_amounts: the rate wins,
 * then a custom percentage, then no VAT. A line that HAS a rate whose
 * percentage is not known yet resolves to null -- never 0% -- and its VAT and
 * gross stay null (shown as PENDING_FIGURE) until the picker resolves the rate
 * and fillPendingVatFigures fills them in.
 *
 * Used by: Outgoing / Proforma / Credit Note / Debit Note / Incoming /
 * Purchase Order Screen2. vatPercentage.everyGridUsesIt.test.js fails if a
 * grid looks the percentage up any other way.
 */

// Incoming's picker offers "Custom %" as a rate: the line then carries
// vat_rate 'CUSTOM' and its percentage in custom_vat_percentage. A saved line
// with a custom percentage comes back from the server as vat_rate null plus
// custom_vat_percentage (vat_rate_display "CUSTOM (13%)" is built from it).
export const CUSTOM_VAT_RATE = 'CUSTOM';

// What a VAT or gross cell shows while the line's percentage is not known.
export const PENDING_FIGURE = '—';

// A percentage as a number, or null when the value holds none ('' / null /
// undefined / unreadable). 0 is a real percentage, not a missing one.
const toPercentage = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

// Some callers hold the rate as the picked object rather than its id.
const rateIdOf = (rate) => (rate && typeof rate === 'object' ? rate.id : rate);

/**
 * @param {object} line   a document line
 * @param {object} cache  rate id -> percentage, filled by the VAT picker
 * @returns {number|null} the percentage, 0 for a line with no rate at all,
 *                        null for a line whose rate is not known yet
 */
export const resolveVatPercentage = (line, cache = {}) => {
  const rate = rateIdOf(line?.vat_rate);

  if (rate === CUSTOM_VAT_RATE) {
    return toPercentage(line.custom_vat_percentage);
  }

  if (rate) {
    const cached = toPercentage(cache?.[parseInt(rate, 10)]);
    if (cached !== null) return cached;
    // Lines whose rate was picked in this session carry their own copy.
    return toPercentage(line.vat_rate_percentage);
  }

  const custom = toPercentage(line?.custom_vat_percentage);
  return custom !== null ? custom : 0;
};

/**
 * A line's VAT and gross from its net and percentage, as strings to the cent:
 * VAT = net x % rounded to 2, gross = net + that VAT. A null percentage
 * leaves both null -- not yet known, never 0.
 */
export const lineVatFigures = (netAmount, vatPercentage) => {
  if (vatPercentage === null || vatPercentage === undefined) {
    return { vat_amount: null, gross_amount: null };
  }
  const net = parseFloat(netAmount) || 0;
  const vatAmount = (net * (vatPercentage / 100)).toFixed(2);
  return { vat_amount: vatAmount, gross_amount: (net + parseFloat(vatAmount)).toFixed(2) };
};

/**
 * Lines left pending (vat_amount null) get their VAT and gross once their
 * percentage is known -- called when the VAT picker resolves a rate. Returns
 * the SAME array when nothing changed, so a state update is a no-op.
 */
export const fillPendingVatFigures = (lines, cache) => {
  if (!Array.isArray(lines)) return lines;
  let changed = false;
  const next = lines.map((line) => {
    if (!line || line.vat_amount !== null) return line;
    const vatPercentage = resolveVatPercentage(line, cache);
    if (vatPercentage === null) return line;
    changed = true;
    return { ...line, ...lineVatFigures(line.net_amount, vatPercentage) };
  });
  return changed ? next : lines;
};

const isMissingFigure = (value) => value === null || value === undefined || value === '';

/**
 * 1-based numbers of the lines that carry a VAT rate (or a custom %) but whose
 * VAT or gross is not worked out yet -- the lines showing PENDING_FIGURE. A
 * save that sends the screen's figures (the purchase order: its server stores
 * them as sent) must refuse these, never send them as 0.
 */
export const linesAwaitingVat = (lines) => (Array.isArray(lines) ? lines : []).reduce((out, line, index) => {
  const hasRate = Boolean(rateIdOf(line?.vat_rate)) || !isMissingFigure(line?.custom_vat_percentage);
  if (hasRate && (isMissingFigure(line.vat_amount) || isMissingFigure(line.gross_amount))) {
    out.push(index + 1);
  }
  return out;
}, []);

/**
 * The VAT and gross a save sends for a line: the figures the grid worked out,
 * or -- for a line whose figures were never worked out -- the one rule's own
 * (a line with no rate: VAT 0.00, gross = net). A line with a rate and no
 * figures is null here, never 0; linesAwaitingVat refuses it first.
 */
export const vatFiguresToSave = (line) => (
  isMissingFigure(line?.vat_amount) || isMissingFigure(line?.gross_amount)
    ? lineVatFigures(line?.net_amount, resolveVatPercentage(line, {}))
    : { vat_amount: line.vat_amount, gross_amount: line.gross_amount }
);
